import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'react-feather';

/**
 * Brand information section — used by both AddTenant and EditTenant.
 *
 * Props:
 *   form      — full tenant form state (object)
 *   errors    — map of field name → error message
 *   onChange  — (fieldName, value) => void
 *   mode      — 'add' | 'edit'
 *   lookups   — useTenantLookups() result
 *   readOnly  — array of field names to render as KV display instead of input
 */
const BrandSection = ({ form, errors = {}, onChange, mode = 'add', lookups, readOnly = [] }) => {
    const { t } = useTranslation();
    const ro = (name) => readOnly.includes(name);
    const v = (name) => form[name] ?? '';

    return (
        <section className="v3-card v3-tenant-section" id="section-brand">
            <header className="v3-tenant-section-head">
                <h3>{t('Brand information')}</h3>
                <span className="v3-label">{mode === 'add' ? t('Step 1 of 3') : t('Brand & identity')}</span>
            </header>

            <div className="v3-sadmin-form-grid">
                {/* Store + Brand name */}
                <label className="v3-field">
                    <span className="v3-label">{t('Store name *')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_store_name')}
                        onChange={(e) => onChange('tenant_store_name', e.target.value)}
                        placeholder={t('The store the public sees')}
                        disabled={ro('tenant_store_name')}
                    />
                    {errors.tenant_store_name && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_store_name}</span>
                    )}
                </label>
                <label className="v3-field">
                    <span className="v3-label">{t('Brand name *')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_brand_name')}
                        onChange={(e) => onChange('tenant_brand_name', e.target.value)}
                        placeholder={t('Parent brand')}
                        disabled={ro('tenant_brand_name')}
                    />
                    {errors.tenant_brand_name && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_brand_name}</span>
                    )}
                </label>

                {/* Group name (free text + suggestions from existing groups) */}
                <label className="v3-field">
                    <span className="v3-label">{t('Group name')}</span>
                    <input
                        className="v3-input"
                        list="brand-group-names"
                        value={v('tenant_group_name')}
                        onChange={(e) => onChange('tenant_group_name', e.target.value)}
                        placeholder={t('Or pick an existing group')}
                        disabled={ro('tenant_group_name')}
                    />
                    <datalist id="brand-group-names">
                        {(lookups?.groupNames || []).map((g, i) => (
                            <option key={i} value={g.tenant_group_name || g} />
                        ))}
                    </datalist>
                </label>

                <label className="v3-field">
                    <span className="v3-label">{t('Email *')}</span>
                    <input
                        className="v3-input"
                        type="email"
                        value={v('tenant_email_id')}
                        onChange={(e) => onChange('tenant_email_id', e.target.value)}
                        placeholder="tenant@example.com"
                        disabled={ro('tenant_email_id')}
                    />
                    {errors.tenant_email_id && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_email_id}</span>
                    )}
                </label>

                {/* Country: read-only KV when editing */}
                {ro('tenant_country_id') ? (
                    <div className="v3-sadmin-kv-item">
                        <span className="v3-sadmin-kv-label">{t('Country')}</span>
                        <span className="v3-sadmin-kv-value">
                            {(lookups?.countries || []).find((c) => c._id === v('tenant_country_id'))?.country_name || '—'}
                        </span>
                    </div>
                ) : (
                    <label className="v3-field">
                        <span className="v3-label">{t('Country *')}</span>
                        <div className="v3-select-wrap">
                            <select
                                className="v3-input v3-select"
                                value={v('tenant_country_id')}
                                onChange={(e) => onChange('tenant_country_id', e.target.value)}
                            >
                                <option value="">{t('Select country…')}</option>
                                {(lookups?.countries || []).map((c) => (
                                    <option key={c._id} value={c._id}>{c.country_name}</option>
                                ))}
                            </select>
                        </div>
                        {errors.tenant_country_id && (
                            <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_country_id}</span>
                        )}
                    </label>
                )}

                {/* Property: read-only KV when editing */}
                {ro('tenant_property_id') ? (
                    <div className="v3-sadmin-kv-item">
                        <span className="v3-sadmin-kv-label">{t('Property')}</span>
                        <span className="v3-sadmin-kv-value">
                            {(lookups?.allProperties || []).find((p) => p._id === v('tenant_property_id'))?.property_name || '—'}
                        </span>
                    </div>
                ) : (
                    <label className="v3-field">
                        <span className="v3-label">{t('Property *')}</span>
                        <div className="v3-select-wrap">
                            <select
                                className="v3-input v3-select"
                                value={v('tenant_property_id')}
                                onChange={(e) => {
                                    const selected = (lookups?.properties || []).find((p) => p._id === e.target.value);
                                    onChange('tenant_property_id', e.target.value);
                                    onChange('property_mall_code', selected?.property_mall_code || '');
                                }}
                                disabled={!v('tenant_country_id')}
                            >
                                <option value="">{v('tenant_country_id') ? t('Select property…') : t('Pick a country first')}</option>
                                {(lookups?.properties || []).map((p) => (
                                    <option key={p._id} value={p._id}>{p.property_name}</option>
                                ))}
                            </select>
                        </div>
                        {errors.tenant_property_id && (
                            <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_property_id}</span>
                        )}
                    </label>
                )}

                <label className="v3-field">
                    <span className="v3-label">{t('Franchisee *')}</span>
                    <div className="v3-select-wrap">
                        <select
                            className="v3-input v3-select"
                            value={v('tenant_franchisee')}
                            onChange={(e) => onChange('tenant_franchisee', e.target.value)}
                            disabled={ro('tenant_franchisee')}
                        >
                            <option value="">{t('Select…')}</option>
                            <option value="true">{t('Yes')}</option>
                            <option value="false">{t('No')}</option>
                        </select>
                    </div>
                    {errors.tenant_franchisee && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_franchisee}</span>
                    )}
                </label>

                <label className="v3-field">
                    <span className="v3-label">{t('Address')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_address')}
                        onChange={(e) => onChange('tenant_address', e.target.value)}
                        placeholder={t('Street / unit')}
                        disabled={ro('tenant_address')}
                    />
                </label>
            </div>

            {mode === 'add' && (
                <label className="v3-field" style={{ marginTop: 16 }}>
                    <span className="v3-label">{t('Logo (optional)')}</span>
                    <input
                        className="v3-input"
                        type="file"
                        accept="image/*"
                        onChange={(e) => onChange('tenant_logo', e.target.files?.[0] || null)}
                    />
                    {form.tenant_logo?.name && (
                        <span className="v3-label" style={{ color: 'var(--v3-text-secondary)', textTransform: 'none' }}>
                            {t('Selected')}: {form.tenant_logo.name}
                        </span>
                    )}
                </label>
            )}
        </section>
    );
};

export const BRAND_FIELDS = {
    required: ['tenant_store_name', 'tenant_brand_name', 'tenant_email_id', 'tenant_country_id', 'tenant_property_id', 'tenant_franchisee'],
    all: ['tenant_store_name', 'tenant_brand_name', 'tenant_group_name', 'tenant_email_id', 'tenant_country_id', 'tenant_property_id', 'tenant_franchisee', 'tenant_address'],
};

export default BrandSection;
